import React, { useEffect, useId, useMemo, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";

/*
 * Jultomten i klassisk målad reklamstil (1930-talets oljemålade jultomte):
 * varmt ansikte i lätt trekvartsvy, glödande äppelröda kinder, plirande blå
 * skrattögon med skrattrynkor, vita yviga ögonbryn, en stor mustasch som
 * svänger upp i spetsarna och ett fylligt, vågigt skägg av lockar. Luvan är
 * i djupröd sammet med tjock pälskant och tofs som hänger ner till höger.
 *
 * Hår och päls byggs av hundratals avsmalnande lockar som genereras med ett
 * fast slumpfrö, så att tomten ser likadan ut varje gång. Ljuset kommer
 * snett uppifrån vänster: varmvitt i ljuset, kallt blågrått i skuggorna.
 *
 * Porträttet är tungt (mjuka skuggor, förskjutningsfilter för mjuka
 * pälskanter och penselkorn), så det renderas EN gång: först till en
 * SVG-bild och sedan, via en canvas, till en bitmapp. Hjulet och släden visar
 * bara bitmappen, så snurr och flygning kostar inget extra.
 *
 * Porträttet ritas i en 400 x 400-ruta. Ansiktets mittlinje ligger på x=196.
 */

type Pt = [number, number];

const f = (n: number) => n.toFixed(1);

/** Deterministisk slump (mulberry32). */
const rng = (seed: number) => () => {
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

/** Mjuk kurva genom punkterna (Catmull-Rom). */
const curve = (pts: Pt[], move = true) => {
  let d = move ? `M${f(pts[0][0])} ${f(pts[0][1])}` : "";
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    d += ` C${f(p1[0] + (p2[0] - p0[0]) / 6)} ${f(p1[1] + (p2[1] - p0[1]) / 6)} ${f(p2[0] - (p3[0] - p1[0]) / 6)} ${f(p2[1] - (p3[1] - p1[1]) / 6)} ${f(p2[0])} ${f(p2[1])}`;
  }
  return d;
};

/** En lockes mittlinje: en penna som vandrar och svänger. */
const walk = (x: number, y: number, heading: number, length: number, steps: number, bend: (t: number) => number): Pt[] => {
  const pts: Pt[] = [[x, y]];
  const step = length / steps;
  let h = heading;
  for (let i = 1; i <= steps; i++) {
    h += bend(i / steps);
    x += Math.cos(h) * step;
    y += Math.sin(h) * step;
    pts.push([x, y]);
  }
  return pts;
};

/** Avsmalnande band längs mittlinjen: tjockast nära roten, spetsig i änden. */
const ribbon = (pts: Pt[], width: number, pointed = false) => {
  const n = pts.length;
  const L: Pt[] = [];
  const R: Pt[] = [];
  pts.forEach((p, i) => {
    const a = pts[Math.max(0, i - 1)];
    const b = pts[Math.min(n - 1, i + 1)];
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const len = Math.hypot(dx, dy) || 1;
    const t = i / (n - 1);
    const w = (width / 2) * Math.pow(Math.sin(Math.PI * (pointed ? 0.04 + 0.96 * t : 0.25 + 0.75 * t)), pointed ? 0.5 : 0.6);
    L.push([p[0] - (dy / len) * w, p[1] + (dx / len) * w]);
    R.push([p[0] + (dy / len) * w, p[1] - (dx / len) * w]);
  });
  R.reverse();
  return `${curve(L)} L${f(R[0][0])} ${f(R[0][1])}${curve(R, false)} Z`;
};

interface Lock {
  pts: Pt[];
  w: number;
  /** Spetsig även vid roten (när roten syns). */
  pointed?: boolean;
}

interface HairTone {
  fill: string;
  shadow: string;
  light: string;
  strand: string;
}

interface PaintOpts {
  /** Oskärpefilter för skuggan. */
  soft?: string;
  /** Antal hårstrån per lock. */
  strands?: number;
  /** Högdager: skarp linje, mjuk (suddig) eller ingen. */
  hi?: "line" | "soft" | "none";
  hiBlur?: string;
}

/**
 * Ritar lockar: skugga (som faller på lockarna bakom), kropp, hårstrån och
 * en högdager på ljussidan.
 */
const paintLocks = (locks: Lock[], tone: HairTone, key: string, { soft, strands = 3, hi = "line", hiBlur }: PaintOpts = {}) =>
  locks.map((l, i) => {
    const d = ribbon(l.pts, l.w, l.pointed);
    const n = l.pts.length;
    const head = l.pts.slice(0, Math.max(2, Math.round(n * 0.85)));
    const [x0, y0] = l.pts[0];
    const [x1, y1] = l.pts[Math.min(2, n - 1)];
    const len = Math.hypot(x1 - x0, y1 - y0) || 1;
    const nx = -(y1 - y0) / len;
    const ny = (x1 - x0) / len;
    const shift = (pts: Pt[], k: number): Pt[] => pts.map(([x, y], j) => [x + nx * k * (1 - j / n), y + ny * k * (1 - j / n)]);
    const lines = Array.from({ length: strands }, (_, k) => (strands === 1 ? 0 : (k / (strands - 1) - 0.5) * 0.66));
    return (
      <g key={`${key}${i}`}>
        <path d={d} fill={tone.shadow} opacity={soft ? 0.7 : 0.5} transform={`translate(${f(l.w * 0.12)} ${f(l.w * 0.26)})`} filter={soft} />
        <path d={d} fill={tone.fill} />
        {strands > 0 && (
          <>
            <path
              d={lines.filter((_, k) => k % 2 === 0).map(k => curve(shift(head, l.w * k))).join(" ")}
              stroke={tone.strand}
              strokeWidth={Math.max(0.5, l.w * 0.035)}
              fill="none"
              opacity={0.45}
              strokeLinecap="round"
            />
            <path
              d={lines.filter((_, k) => k % 2 === 1).map(k => curve(shift(head, l.w * k))).join(" ")}
              stroke={tone.light}
              strokeWidth={Math.max(0.5, l.w * 0.035)}
              fill="none"
              opacity={0.7}
              strokeLinecap="round"
            />
          </>
        )}
        {hi !== "none" && (
          <path
            d={curve(head.slice(0, Math.max(2, Math.round(head.length * (hi === "soft" ? 0.7 : 1)))).map(([x, y]) => [x - l.w * 0.17, y - l.w * 0.1] as Pt))}
            stroke={tone.light}
            strokeWidth={l.w * (hi === "soft" ? 0.3 : 0.16)}
            fill="none"
            opacity={hi === "soft" ? 0.7 : 0.9}
            strokeLinecap="round"
            filter={hi === "soft" ? hiBlur : undefined}
          />
        )}
      </g>
    );
  });

interface Clump {
  x: number;
  y: number;
  r: number;
}

/** Päls som mjuka bomullstussar: skugga, kropp och en suddig ljuspunkt. */
const paintClumps = (clumps: Clump[], fill: string, shadow: string, blur: (n: number) => string, key: string) =>
  clumps.map((c, i) => (
    <g key={`${key}${i}`}>
      <circle cx={c.x + c.r * 0.22} cy={c.y + c.r * 0.34} r={c.r} fill={shadow} opacity={0.55} filter={blur(4)} />
      <circle cx={c.x} cy={c.y} r={c.r} fill={fill} />
      <ellipse cx={c.x - c.r * 0.3} cy={c.y - c.r * 0.36} rx={c.r * 0.55} ry={c.r * 0.34} fill="#ffffff" opacity={0.75} filter={blur(2.4)} />
    </g>
  ));

/** Vågig lock: S-svängar längs vägen och en krulla i spetsen. */
const wave = (rand: () => number, amp: number, curl: number, steps: number, curlDir?: number) => {
  const phase = rand() * Math.PI * 2;
  const freq = 0.7 + rand() * 0.7;
  const dir = curlDir ?? (rand() < 0.5 ? -1 : 1);
  return (t: number) => (amp * Math.sin(Math.PI * 2 * freq * t + phase) + (t > 0.58 ? dir * curl * (t - 0.58) * 2.4 : 0)) * (10 / steps);
};

/* ------------------------------------------------------------------ */
/* Generering av skägg, mustasch, ögonbryn och päls                     */
/* ------------------------------------------------------------------ */

const MID = 196;

const buildHair = () => {
  const rand = rng(1931);
  const steps = 14;

  // Bakre skägget: mörkare lockar som ger djup mellan de främre
  const back: Lock[] = [];
  for (let row = 0; row < 4; row++) {
    const y = 262 + row * 40;
    for (let x = 70 + (row % 2) * 16; x <= 330; x += 30) {
      const fan = ((x - MID) / 140) * 0.4;
      back.push({
        pts: walk(x + (rand() - 0.5) * 12, y + (rand() - 0.5) * 14, Math.PI / 2 - fan, 90 + rand() * 40, steps, wave(rand, 0.2, 0.9, steps, x < MID ? 1 : -1)),
        w: 30 + rand() * 10,
      });
    }
  }

  // Främre skägget, nedifrån och upp så att övre lockar faller över de undre
  const front: Lock[] = [];
  const rows = [
    { y: 398, x0: 64, x1: 330, step: 26, len: [70, 90], w: [28, 36] },
    { y: 368, x0: 58, x1: 338, step: 24, len: [75, 100], w: [28, 36] },
    { y: 340, x0: 74, x1: 322, step: 22, len: [80, 105], w: [26, 34] },
  ];
  rows.forEach(r => {
    for (let x = r.x0; x <= r.x1; x += r.step) {
      const fan = ((x - MID) / 150) * 0.55;
      front.push({
        pts: walk(x + (rand() - 0.5) * 8, r.y + (rand() - 0.5) * 12, Math.PI / 2 - fan, r.len[0] + rand() * (r.len[1] - r.len[0]), steps, wave(rand, 0.32, 1.4, steps, x < MID ? 1 : -1)),
        w: r.w[0] + rand() * (r.w[1] - r.w[0]),
      });
    }
  });
  // Hakskägget direkt under underläppen
  for (let x = 156; x <= 240; x += 14) {
    front.push({
      pts: walk(x + (rand() - 0.5) * 4, 334 + Math.abs(x - MID) * 0.06, Math.PI / 2 - ((x - MID) / 60) * 0.3, 60 + rand() * 25, steps, wave(rand, 0.22, 1, steps, x < MID ? 1 : -1)),
      w: 22 + rand() * 6,
    });
  }

  // Polisonger och skägg längs käken, båda sidor
  const sides: Lock[] = [];
  const leftRoots: Pt[] = [[102, 178], [97, 202], [94, 226], [94, 250], [97, 274], [106, 296], [120, 314], [138, 326]];
  const rightRoots: Pt[] = [[292, 176], [298, 200], [301, 224], [301, 248], [298, 272], [290, 294], [276, 312], [258, 324]];
  leftRoots.forEach(([x, y], i) =>
    sides.push({
      pts: walk(x, y, Math.PI / 2 + 0.34 - i * 0.05, 78 + rand() * 26, steps, wave(rand, 0.2, 1, steps, 1)),
      w: 26 + rand() * 6,
    })
  );
  rightRoots.forEach(([x, y], i) =>
    sides.push({
      pts: walk(x, y, Math.PI / 2 - 0.34 + i * 0.05, 78 + rand() * 26, steps, wave(rand, 0.2, 1, steps, -1)),
      w: 26 + rand() * 6,
    })
  );
  // Skägg vid mungiporna, under mustaschens spetsar
  ([[128, 296], [138, 312], [150, 326], [264, 294], [256, 310], [244, 324]] as Pt[]).forEach(([x, y]) =>
    sides.push({
      pts: walk(x, y, Math.PI / 2 + (x < MID ? 0.2 : -0.2), 62 + rand() * 20, steps, wave(rand, 0.2, 1, steps, x < MID ? 1 : -1)),
      w: 24 + rand() * 5,
    })
  );

  // Mustaschen: två kraftiga svepande halvor som svänger upp i spetsarna.
  // Yttre (lägre) lockar ritas först, de översta sist.
  const stache: Lock[] = [];
  const bendL = (t: number) => (t < 0.35 ? -0.05 : 0.2) * (10 / steps);
  for (let i = 0; i < 9; i++) {
    const k = i / 8;
    const len = 104 - k * 30 + rand() * 10;
    const y = 262 - k * 3;
    stache.push({ pts: walk(MID - 3 - k * 18, y, Math.PI - 0.44 + k * 0.12, len, steps, bendL), w: 25 - k * 5 + rand() * 3 });
    stache.push({ pts: walk(MID + 3 + k * 18, y - 1, 0.44 - k * 0.12, len + 4, steps, t => -bendL(t)), w: 26 - k * 5 + rand() * 3 });
  }

  // Yviga ögonbryn i två lager
  const brows: Lock[] = [];
  const leftBrow: Pt[] = [[178, 200], [170, 194], [161, 190], [152, 188], [143, 189], [134, 192], [126, 196]];
  const rightBrow: Pt[] = [[216, 198], [225, 192], [234, 188], [243, 187], [252, 188], [261, 191], [269, 196]];
  for (let layer = 0; layer < 3; layer++) {
    leftBrow.forEach(([x, y], i) =>
      brows.push({ pts: walk(x + layer * 2 + rand() * 3, y - layer * 2.5, Math.PI + 0.55 - i * 0.15 + (rand() - 0.5) * 0.2, 24 + rand() * 12 - layer * 4, 7, () => -0.08), w: 9 + rand() * 3 - layer, pointed: true })
    );
    rightBrow.forEach(([x, y], i) =>
      brows.push({ pts: walk(x - layer * 2 - rand() * 3, y - layer * 2.5, -0.55 + i * 0.15 + (rand() - 0.5) * 0.2, 24 + rand() * 12 - layer * 4, 7, () => 0.08), w: 9 + rand() * 3 - layer, pointed: true })
    );
  }

  // Pälskanten: fluffiga tussar i ett brett band över pannan
  const top: Pt[] = [[70, 140], [130, 112], [200, 100], [262, 98], [330, 114]];
  const bottom: Pt[] = [[80, 188], [138, 166], [200, 158], [260, 156], [322, 168]];
  const lerpEdge = (edge: Pt[], u: number): Pt => {
    const sI = u * (edge.length - 1);
    const i = Math.min(edge.length - 2, Math.floor(sI));
    const t = sI - i;
    return [edge[i][0] + (edge[i + 1][0] - edge[i][0]) * t, edge[i][1] + (edge[i + 1][1] - edge[i][1]) * t];
  };
  const fur: Clump[] = [];
  for (let v = 0; v < 4; v++) {
    const n = 15 + (v % 2);
    for (let j = 0; j < n; j++) {
      const uu = Math.min(1, Math.max(0, (j + (v % 2) * 0.5 + (rand() - 0.5) * 0.5) / (n - 1)));
      const a = lerpEdge(top, uu);
      const b = lerpEdge(bottom, uu);
      const vv = 0.12 + (v / 3) * 0.78 + (rand() - 0.5) * 0.1;
      fur.push({ x: a[0] + (b[0] - a[0]) * vv, y: a[1] + (b[1] - a[1]) * vv, r: 11 + rand() * 6 });
    }
  }
  // Små fjun längs kanterna så att konturen blir luftig
  const wisps: Lock[] = [];
  for (let j = 0; j <= 30; j++) {
    const uu = j / 30;
    const [bx, by] = lerpEdge(bottom, uu);
    wisps.push({ pts: walk(bx + (rand() - 0.5) * 6, by - 4, Math.PI / 2 + (rand() - 0.5) * 1.2, 9 + rand() * 7, 5, wave(rand, 0.3, 0.8, 5)), w: 6 + rand() * 3, pointed: true });
    const [tx, ty] = lerpEdge(top, uu);
    if (j % 2 === 0) wisps.push({ pts: walk(tx + (rand() - 0.5) * 6, ty + 4, -Math.PI / 2 + (rand() - 0.5) * 1.4, 7 + rand() * 6, 5, wave(rand, 0.3, 0.8, 5)), w: 5 + rand() * 3, pointed: true });
  }

  // Tofsen: tussar i ett klot, de upplysta överst till vänster ritas sist
  const pom: Clump[] = [];
  for (let i = 0; i < 16; i++) {
    const ang = rand() * Math.PI * 2;
    const rr = Math.sqrt(rand()) * 18;
    pom.push({ x: 330 + Math.cos(ang) * rr, y: 284 + Math.sin(ang) * rr, r: 9 + rand() * 5 });
  }
  pom.sort((p1, p2) => p2.x + p2.y - (p1.x + p1.y));

  return {
    back,
    front,
    sides,
    stache,
    brows,
    fur,
    wisps,
    pom,
    band: `${curve(top)} C 348 122, 348 166, ${f(bottom[4][0])} ${f(bottom[4][1])} ${curve([...bottom].reverse(), false)} C 60 188, 54 146, ${f(top[0][0])} ${f(top[0][1])} Z`,
  };
};

/* ------------------------------------------------------------------ */
/* Ögon                                                                 */
/* ------------------------------------------------------------------ */

const SmileEye: React.FC<{ x: number; y: number; w: number; outer: -1 | 1; id: string; blur: (n: number) => string }> = ({ x, y, w, outer, id, blur }) => {
  const h = w / 2;
  const clip = `eye-${id}-${outer}`;
  // Ögonspringan: platt underkant som trycks upp av kinden, välvd överkant
  const shape = `M${x - h} ${y + 1} C ${x - h * 0.55} ${y - 10}, ${x + h * 0.45} ${y - 11.5}, ${x + h} ${y - 2} C ${x + h * 0.45} ${y + 4.5}, ${x - h * 0.5} ${y + 6}, ${x - h} ${y + 1} Z`;
  // Blicken mot betraktaren
  const ix = x - 1.5;
  const iy = y - 2;
  const ox = outer === -1 ? x - h : x + h;
  return (
    <g>
      <clipPath id={clip}>
        <path d={shape} />
      </clipPath>
      {/* Ögonhålan och ögonbrynens skugga */}
      <ellipse cx={x} cy={y - 5} rx={h + 9} ry={13} fill="#9a3f2e" opacity={0.42} filter={blur(4)} />
      <path d={shape} fill="#f3e7df" />
      <g clipPath={`url(#${clip})`}>
        <circle cx={ix} cy={iy} r={8.6} fill={`url(#iris-${id})`} />
        <circle cx={ix} cy={iy} r={8.6} fill="none" stroke="#1b2a42" strokeWidth={1.3} opacity={0.85} />
        <circle cx={ix} cy={iy} r={3.8} fill="#0a0e17" />
        {/* Ögonlockets skugga över ögongloben */}
        <path d={`M${x - h} ${y - 14} L ${x + h} ${y - 16} L ${x + h} ${y - 3} C ${x + h * 0.4} ${y - 7}, ${x - h * 0.5} ${y - 6}, ${x - h} ${y} Z`} fill="#5e2218" opacity={0.5} filter={blur(1.6)} />
        <path d={`M${x - h} ${y + 6} C ${x - h * 0.4} ${y + 2}, ${x + h * 0.4} ${y + 1}, ${x + h} ${y + 4}`} stroke="#c8a89c" strokeWidth={2} fill="none" opacity={0.5} />
      </g>
      {/* Ljusglimtar – plirandet */}
      <circle cx={ix - 3} cy={iy - 2.8} r={2.3} fill="#ffffff" />
      <circle cx={ix + 3.4} cy={iy + 1.8} r={1} fill="#ffffff" opacity={0.75} />
      {/* Fransar och övre ögonlock */}
      <path d={`M${x - h - 1.5} ${y + 1.6} C ${x - h * 0.55} ${y - 10.4}, ${x + h * 0.45} ${y - 12}, ${x + h + 1.5} ${y - 1.4}`} stroke="#34120c" strokeWidth={2.8} fill="none" strokeLinecap="round" />
      <path d={`M${x - h - 3} ${y - 4} C ${x - h * 0.4} ${y - 17.5}, ${x + h * 0.4} ${y - 18.5}, ${x + h + 3} ${y - 7}`} stroke="#86331f" strokeWidth={1.8} fill="none" opacity={0.6} strokeLinecap="round" />
      <path d={`M${x - h} ${y - 8} C ${x - h * 0.4} ${y - 16}, ${x + h * 0.4} ${y - 17}, ${x + h} ${y - 9}`} stroke="#ffdcc8" strokeWidth={2.6} fill="none" opacity={0.45} filter={blur(1)} />
      {/* Underlocket och kinden som trycks upp */}
      <path d={`M${x - h + 2} ${y + 3} C ${x - h * 0.3} ${y + 7}, ${x + h * 0.4} ${y + 5}, ${x + h - 1} ${y - 1}`} stroke="#94402f" strokeWidth={1.4} fill="none" opacity={0.6} strokeLinecap="round" />
      <path d={`M${x - h + 1} ${y + 8.5} C ${x - h * 0.3} ${y + 13.5}, ${x + h * 0.4} ${y + 12}, ${x + h} ${y + 5}`} stroke="#ffe3d4" strokeWidth={4} fill="none" opacity={0.6} filter={blur(1.6)} />
      <path d={`M${x - h} ${y + 13} C ${x - h * 0.3} ${y + 18}, ${x + h * 0.4} ${y + 16.5}, ${x + h + 1} ${y + 9}`} stroke="#9a4432" strokeWidth={1.6} fill="none" opacity={0.4} strokeLinecap="round" filter={blur(1)} />
      {/* Skrattrynkor i ögonvrån */}
      <g stroke="#8a3626" strokeWidth={1.6} fill="none" strokeLinecap="round" opacity={0.6} filter={blur(1)}>
        <path d={`M${ox + outer * 2} ${y - 4} q ${outer * 7} -3 ${outer * 13} -9`} />
        <path d={`M${ox + outer * 3} ${y + 1} q ${outer * 7} 0 ${outer * 15} -1`} />
        <path d={`M${ox + outer * 2} ${y + 6} q ${outer * 7} 3 ${outer * 12} 9`} />
      </g>
      <g stroke="#ffe6d8" strokeWidth={1.4} fill="none" strokeLinecap="round" opacity={0.65}>
        <path d={`M${ox + outer * 3} ${y - 1.5} q ${outer * 7} -2 ${outer * 12} -6`} />
        <path d={`M${ox + outer * 3} ${y + 3.5} q ${outer * 7} 1.5 ${outer * 13} 5`} />
      </g>
    </g>
  );
};

/* ------------------------------------------------------------------ */
/* Porträttet (vektor)                                                  */
/* ------------------------------------------------------------------ */

const FACE =
  "M104 160 C 100 200, 100 250, 112 286 C 124 318, 160 338, 196 338 C 234 338, 270 318, 284 286 C 298 252, 300 200, 294 156 Z";

const SantaArt: React.FC = () => {
  const id = useId().replace(/:/g, "");
  const u = (name: string) => `url(#${name}-${id})`;
  const blur = (n: number) => u(`b${String(n).replace(".", "_")}`);
  const hair = useMemo(buildHair, []);

  const beardTone: HairTone = { fill: u("hair"), shadow: "#5f6a83", light: "#ffffff", strand: "#8993a8" };
  const backTone: HairTone = { fill: u("hairBack"), shadow: "#3f475c", light: "#e3e7ee", strand: "#6f7a91" };
  const browTone: HairTone = { fill: u("brow"), shadow: "#6e5a54", light: "#ffffff", strand: "#8e8480" };
  const furTone: HairTone = { fill: u("fur"), shadow: "#6f7990", light: "#ffffff", strand: "#aab2c3" };
  const pomTone: HairTone = { fill: u("pom"), shadow: "#646e86", light: "#ffffff", strand: "#aab2c3" };
  const soft = blur(4);

  return (
    <g>
      <defs>
        {[1, 1.6, 2.4, 4, 6, 9, 14].map(n => (
          <filter key={n} id={`b${String(n).replace(".", "_")}-${id}`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation={n} />
          </filter>
        ))}
        {/* Fluffiga, lite oregelbundna kanter på päls och hår */}
        <filter id={`wisp-${id}`} x="-10%" y="-10%" width="120%" height="120%">
          <feTurbulence type="fractalNoise" baseFrequency="0.045" numOctaves="2" seed="4" result="t" />
          <feDisplacementMap in="SourceGraphic" in2="t" scale="3.5" xChannelSelector="R" yChannelSelector="G" />
        </filter>
        <filter id={`fluff-${id}`} x="-10%" y="-10%" width="120%" height="120%">
          <feTurbulence type="fractalNoise" baseFrequency="0.09" numOctaves="3" seed="9" result="t" />
          <feDisplacementMap in="SourceGraphic" in2="t" scale="6" xChannelSelector="R" yChannelSelector="G" />
        </filter>
        <radialGradient id={`skin-${id}`} gradientUnits="userSpaceOnUse" cx="160" cy="205" r="170">
          <stop offset="0%" stopColor="#ffeadf" />
          <stop offset="34%" stopColor="#f9c3ac" />
          <stop offset="70%" stopColor="#e8977f" />
          <stop offset="100%" stopColor="#ae5448" />
        </radialGradient>
        <radialGradient id={`nose-${id}`} cx="36%" cy="30%" r="72%">
          <stop offset="0%" stopColor="#ffe8dc" />
          <stop offset="30%" stopColor="#f9b09c" />
          <stop offset="70%" stopColor="#e8766e" />
          <stop offset="100%" stopColor="#b3434a" />
        </radialGradient>
        <radialGradient id={`wing-${id}`} cx="45%" cy="35%" r="65%">
          <stop offset="0%" stopColor="#f6a893" />
          <stop offset="70%" stopColor="#e07a6a" />
          <stop offset="100%" stopColor="#c45a50" stopOpacity="0.9" />
        </radialGradient>
        <radialGradient id={`cheek-${id}`} cx="50%" cy="55%" r="50%">
          <stop offset="0%" stopColor="#e82e28" stopOpacity="0.85" />
          <stop offset="50%" stopColor="#e2463a" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#e86a52" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`iris-${id}`} cx="42%" cy="40%" r="65%">
          <stop offset="0%" stopColor="#a8d0f4" />
          <stop offset="45%" stopColor="#4f80bb" />
          <stop offset="100%" stopColor="#1c355c" />
        </radialGradient>
        <linearGradient id={`lip-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#e2454b" />
          <stop offset="55%" stopColor="#c02a33" />
          <stop offset="100%" stopColor="#861620" />
        </linearGradient>
        <linearGradient id={`teeth-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fffdf8" />
          <stop offset="70%" stopColor="#f1e9df" />
          <stop offset="100%" stopColor="#cdbfb0" />
        </linearGradient>
        <radialGradient id={`hair-${id}`} gradientUnits="userSpaceOnUse" cx="150" cy="240" r="250">
          <stop offset="0%" stopColor="#fffdf9" />
          <stop offset="40%" stopColor="#f4f2ee" />
          <stop offset="70%" stopColor="#cfd4de" />
          <stop offset="100%" stopColor="#8a94aa" />
        </radialGradient>
        <linearGradient id={`brow-${id}`} gradientUnits="userSpaceOnUse" x1="0" y1="180" x2="0" y2="204">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="60%" stopColor="#ecebea" />
          <stop offset="100%" stopColor="#b8b2b0" />
        </linearGradient>
        <radialGradient id={`hairBack-${id}`} gradientUnits="userSpaceOnUse" cx="150" cy="240" r="250">
          <stop offset="0%" stopColor="#d8dce4" />
          <stop offset="60%" stopColor="#a1a9ba" />
          <stop offset="100%" stopColor="#626c83" />
        </radialGradient>
        <radialGradient id={`fur-${id}`} gradientUnits="userSpaceOnUse" cx="140" cy="118" r="235">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="45%" stopColor="#f3f2ef" />
          <stop offset="78%" stopColor="#cdd2dc" />
          <stop offset="100%" stopColor="#9098ac" />
        </radialGradient>
        <radialGradient id={`pom-${id}`} gradientUnits="userSpaceOnUse" cx="316" cy="268" r="56">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="50%" stopColor="#ecebee" />
          <stop offset="100%" stopColor="#8f98ac" />
        </radialGradient>
        <linearGradient id={`band-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#eeeeec" />
          <stop offset="55%" stopColor="#c9ced9" />
          <stop offset="100%" stopColor="#7e879c" />
        </linearGradient>
        <radialGradient id={`velvet-${id}`} gradientUnits="userSpaceOnUse" cx="150" cy="60" r="290">
          <stop offset="0%" stopColor="#ff6a55" />
          <stop offset="26%" stopColor="#e3262b" />
          <stop offset="60%" stopColor="#a8101b" />
          <stop offset="100%" stopColor="#4a040b" />
        </radialGradient>
        <clipPath id={`face-${id}`}>
          <path d={FACE} />
        </clipPath>
      </defs>

      {/* Luvan: toppen viker sig åt höger och hänger ner bakom huvudet */}
      <path d="M86 146 C 80 90, 124 40, 196 28 C 262 18, 324 46, 352 104 C 374 150, 372 220, 348 276 L 318 272 C 330 226, 330 176, 314 136 Z" fill={u("velvet")} />
      <g>
        <path d="M232 40 C 280 52, 312 92, 322 150 C 326 190, 324 230, 318 262" stroke="#4e040b" strokeWidth={16} fill="none" opacity={0.55} filter={blur(9)} />
        <path d="M118 100 C 146 62, 188 44, 238 42" stroke="#ff8f7a" strokeWidth={12} fill="none" opacity={0.5} filter={blur(6)} strokeLinecap="round" />
        <path d="M150 72 C 176 54, 206 48, 234 48" stroke="#ffc8b6" strokeWidth={3.4} fill="none" opacity={0.6} filter={blur(1.6)} strokeLinecap="round" />
        <path d="M286 70 C 322 100, 346 150, 348 214" stroke="#ff6e5c" strokeWidth={7} fill="none" opacity={0.42} filter={blur(4)} strokeLinecap="round" />
        <path d="M198 58 C 238 78, 262 108, 270 134" stroke="#6a0610" strokeWidth={9} fill="none" opacity={0.4} filter={blur(6)} strokeLinecap="round" />
        <path d="M100 150 C 104 120, 116 96, 134 78" stroke="#6a0610" strokeWidth={8} fill="none" opacity={0.35} filter={blur(6)} strokeLinecap="round" />
      </g>

      {/* Bakre skägget */}
      <g filter={u("wisp")}>
        <path d="M98 172 C 72 212, 60 262, 58 306 C 54 356, 54 402, 82 430 C 130 448, 270 448, 318 430 C 346 402, 346 356, 342 306 C 340 262, 328 212, 302 168 Z" fill={u("hairBack")} />
        {paintLocks(hair.back, backTone, "bk", { soft, strands: 3, hi: "soft", hiBlur: blur(2.4) })}
      </g>

      {/* Ansiktet */}
      <path d={FACE} fill={u("skin")} />
      <g clipPath={`url(#face-${id})`}>
        <ellipse cx="180" cy="184" rx="66" ry="14" fill="#fff2e6" opacity={0.55} filter={blur(9)} />
        <ellipse cx="200" cy="164" rx="130" ry="14" fill="#6e2016" opacity={0.6} filter={blur(6)} />
        <ellipse cx="300" cy="240" rx="28" ry="100" fill="#83301f" opacity={0.55} filter={blur(14)} />
        <ellipse cx="100" cy="236" rx="12" ry="70" fill="#94412e" opacity={0.35} filter={blur(9)} />
        {/* Äppelröda kinder med blank glans */}
        <ellipse cx="130" cy="258" rx="50" ry="44" fill={u("cheek")} />
        <ellipse cx="264" cy="256" rx="52" ry="46" fill={u("cheek")} />
        <ellipse cx="134" cy="266" rx="26" ry="20" fill="#c81e22" opacity={0.3} filter={blur(6)} />
        <ellipse cx="266" cy="264" rx="28" ry="21" fill="#b81a20" opacity={0.3} filter={blur(6)} />
        <ellipse cx="120" cy="241" rx="12" ry="7" fill="#fff3ec" opacity={0.8} filter={blur(2.4)} transform="rotate(-18 120 241)" />
        <ellipse cx="250" cy="239" rx="13" ry="7" fill="#fff3ec" opacity={0.65} filter={blur(2.4)} transform="rotate(-18 250 239)" />
        <circle cx="117" cy="239" r="2.2" fill="#ffffff" opacity={0.8} />
        {/* Skrattveck från näsvingarna */}
        <path d="M168 264 C 156 276, 148 290, 146 306" stroke="#80281c" strokeWidth={6} fill="none" opacity={0.45} filter={blur(2.4)} />
        <path d="M224 262 C 236 274, 244 288, 248 304" stroke="#80281c" strokeWidth={6} fill="none" opacity={0.45} filter={blur(2.4)} />
        <path d="M163 260 C 151 272, 142 286, 139 302" stroke="#ffd7c4" strokeWidth={3} fill="none" opacity={0.45} filter={blur(1.6)} />
      </g>

      <SmileEye x={150} y={214} w={43} outer={-1} id={id} blur={blur} />
      <SmileEye x={242} y={212} w={45} outer={1} id={id} blur={blur} />

      {/* Munnen: brett skratt med tänder och full underläpp */}
      <path d="M146 283 C 170 278, 224 277, 250 282 C 244 304, 224 316, 198 316 C 172 316, 152 304, 146 283 Z" fill="#360a0c" />
      <ellipse cx="200" cy="312" rx="28" ry="8" fill="#a33038" opacity={0.75} />
      <path d="M152 284 C 178 280, 220 279, 244 283 L 240 297 C 216 301, 180 301, 157 297 Z" fill={u("teeth")} />
      <path d="M168 286 V 299.4 M 181 285 V 300.6 M 194 284.6 V 301 M 207 284.6 V 301 M 220 285 V 300.4 M 232 285.6 V 299.4" stroke="#c4b6a6" strokeWidth={0.9} opacity={0.75} />
      <path d="M153 287 C 158 292, 160 296, 158 298 M 243 286 C 239 292, 239 296, 239 298" stroke="#5e2220" strokeWidth={3.4} fill="none" opacity={0.55} filter={blur(1)} />
      <path d="M156 308 C 176 320, 222 320, 242 306 C 238 322, 222 330, 198 330 C 174 330, 160 322, 156 308 Z" fill={u("lip")} />
      <ellipse cx="192" cy="320" rx="16" ry="3.4" fill="#ffa6a0" opacity={0.75} filter={blur(1.6)} />
      <path d="M146 284 q 2 6 -1 11 M 250 283 q -2 6 1 11" stroke="#83301f" strokeWidth={2.2} fill="none" strokeLinecap="round" opacity={0.6} />

      {/* Skägg längs käken och under hakan */}
      <g filter={u("wisp")}>
        <ellipse cx="198" cy="342" rx="46" ry="8" fill="#6e3a34" opacity={0.45} filter={blur(4)} />
        {paintLocks(hair.front, beardTone, "fr", { soft, strands: 5, hi: "soft", hiBlur: blur(2.4) })}
        {paintLocks(hair.sides, beardTone, "sd", { soft, strands: 5, hi: "soft", hiBlur: blur(2.4) })}
        {/* Skäggets volym: ljus till vänster, skugga nedtill och till höger */}
        <ellipse cx="330" cy="390" rx="60" ry="80" fill="#3e4862" opacity={0.4} filter={blur(14)} />
        <ellipse cx="200" cy="440" rx="170" ry="36" fill="#3e4862" opacity={0.35} filter={blur(14)} />
        <ellipse cx="96" cy="320" rx="30" ry="70" fill="#ffffff" opacity={0.3} filter={blur(14)} />
      </g>

      {/* Mustasch */}
      <g>
        <ellipse cx="197" cy="276" rx="60" ry="11" fill="#50201c" opacity={0.45} filter={blur(4)} />
        {paintLocks(hair.stache, beardTone, "st", { soft, strands: 5, hi: "soft", hiBlur: blur(2.4) })}
      </g>

      {/* Näsan ligger framför mustaschen: bred, rund och rosig med blank topp */}
      <ellipse cx="201" cy="272" rx="30" ry="7" fill="#5c6680" opacity={0.5} filter={blur(4)} />
      <path d="M198 204 C 196 220, 194 230, 191 238" stroke="#ffe6d6" strokeWidth={8} fill="none" opacity={0.5} filter={blur(2.4)} strokeLinecap="round" />
      <path d="M212 210 C 218 226, 221 238, 219 248" stroke="#a24a36" strokeWidth={8} fill="none" opacity={0.45} filter={blur(2.4)} />
      <path d="M180 214 C 176 226, 172 236, 170 246" stroke="#b25a44" strokeWidth={6} fill="none" opacity={0.3} filter={blur(2.4)} />
      <ellipse cx="174" cy="257" rx="14" ry="11" fill={u("wing")} filter={blur(1)} />
      <ellipse cx="219" cy="255" rx="14.5" ry="11" fill={u("wing")} filter={blur(1)} />
      <path d="M165 262 q 8 5 17 2 M 228 261 q -8 5 -17 2" stroke="#5a1416" strokeWidth={3} fill="none" strokeLinecap="round" opacity={0.6} filter={blur(1)} />
      <ellipse cx="196" cy="246" rx="24" ry="21" fill={u("nose")} />
      <path d="M178 258 C 186 266, 208 267, 216 256" stroke="#ffb2a2" strokeWidth={2.4} fill="none" opacity={0.45} filter={blur(1)} />
      <ellipse cx="204" cy="257" rx="16" ry="5.6" fill="#8e1e28" opacity={0.3} filter={blur(2.4)} />
      <ellipse cx="187" cy="236" rx="8.6" ry="5.4" fill="#ffffff" opacity={0.85} filter={blur(1.6)} />
      <circle cx="185" cy="235" r="2.1" fill="#ffffff" />
      <ellipse cx="166" cy="252" rx="3.4" ry="2.2" fill="#ffffff" opacity={0.6} filter={blur(1)} />

      {/* Ögonbryn */}
      <g>{paintLocks(hair.brows, browTone, "br", { soft: blur(1.6), strands: 3, hi: "none" })}</g>

      {/* Pälskanten och tofsen */}
      <g filter={u("fluff")}>
        <path d={hair.band} fill={u("band")} />
        <path d="M86 176 C 140 156, 260 146, 318 160" stroke="#6f7990" strokeWidth={16} fill="none" opacity={0.45} filter={blur(6)} />
        {paintClumps(hair.fur, u("fur"), furTone.shadow, blur, "fu")}
        {paintLocks(hair.wisps, furTone, "fw", { soft: blur(1.6), strands: 0, hi: "none" })}
        <ellipse cx="150" cy="122" rx="70" ry="10" fill="#ffffff" opacity={0.55} filter={blur(6)} transform="rotate(-10 150 122)" />
      </g>
      <g filter={u("fluff")}>
        <circle cx="332" cy="288" r="28" fill={pomTone.shadow} opacity={0.6} filter={blur(6)} />
        <circle cx="330" cy="284" r="26" fill={u("pom")} />
        {paintClumps(hair.pom, u("pom"), pomTone.shadow, blur, "po")}
      </g>
    </g>
  );
};

/* ------------------------------------------------------------------ */
/* Bildcache                                                            */
/* ------------------------------------------------------------------ */

const svgCache = new Map<string, string>();
const pngCache = new Map<string, Promise<string>>();
const pngReady = new Map<string, string>();

/** Renderar en tung SVG-målning till en bild-URL (en gång per nyckel). */
const paintedSvg = (key: string, art: () => React.ReactElement, height: number) => {
  let url = svgCache.get(key);
  if (!url) {
    const markup = renderToStaticMarkup(
      <svg xmlns="http://www.w3.org/2000/svg" viewBox={`0 0 400 ${height}`} width="400" height={height}>
        {art()}
      </svg>
    );
    url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
    svgCache.set(key, url);
  }
  return url;
};

/** Rastrerar SVG-bilden till en bitmapp i dubbel upplösning, så att filtren bara körs en gång. */
const rasterize = (key: string, svgUrl: string, height: number, scale = 2) => {
  let job = pngCache.get(key);
  if (!job) {
    job = new Promise<string>((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = 400 * scale;
        canvas.height = height * scale;
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject(new Error("no canvas"));
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        // Data-URL (inte blob:) så att bilden följer med när hjulet sparas som bild
        resolve(canvas.toDataURL("image/webp", 0.92));
      };
      img.onerror = reject;
      img.src = svgUrl;
    }).then(url => {
      pngReady.set(key, url);
      return url;
    });
    pngCache.set(key, job);
  }
  return job;
};

/** Målningens URL: SVG-bilden direkt, bitmappen så snart den är klar. */
const usePainted = (key: string, art: () => React.ReactElement, height = 400) => {
  const svgUrl = paintedSvg(key, art, height);
  const [url, setUrl] = useState(() => pngReady.get(key) ?? svgUrl);
  useEffect(() => {
    let live = true;
    rasterize(key, svgUrl, height)
      .then(png => live && setUrl(png))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [key, svgUrl, height]);
  return url;
};

/** Porträttbilden är högre än rutan så att skäggets lockar får sluta fritt. */
const PORTRAIT_H = 520;

/** Tomtens huvud med hela skägget, för släden. `size` är bredden (400-rutan). */
export const SantaPortrait: React.FC<{ x?: number; y?: number; size?: number }> = ({ x = 0, y = 0, size = 400 }) => (
  <image href={usePainted("portrait", () => <SantaArt />, PORTRAIT_H)} x={x} y={y} width={size} height={(size * PORTRAIT_H) / 400} />
);

/* ------------------------------------------------------------------ */
/* Medaljong för hjulets mitt                                           */
/* ------------------------------------------------------------------ */

const BustArt: React.FC = () => {
  const id = useId().replace(/:/g, "");
  const u = (name: string) => `url(#${name}-${id})`;
  const rand = rng(1225);
  const bokeh = Array.from({ length: 24 }, () => ({
    x: 16 + rand() * 368,
    y: 16 + rand() * 260,
    r: 4 + rand() * 15,
    warm: rand() < 0.72,
    o: 0.22 + rand() * 0.35,
  }));
  const snow = Array.from({ length: 46 }, () => ({ x: rand() * 400, y: rand() * 400, r: 0.8 + rand() * 2.2, o: 0.4 + rand() * 0.5 }));
  return (
    <g>
      <defs>
        <clipPath id={`disc-${id}`}>
          <circle cx="200" cy="200" r="186" />
        </clipPath>
        <linearGradient id={`night-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0a1a32" />
          <stop offset="55%" stopColor="#1b3a5e" />
          <stop offset="100%" stopColor="#2e5174" />
        </linearGradient>
        <radialGradient id={`glow-${id}`} cx="28%" cy="28%" r="70%">
          <stop offset="0%" stopColor="#ffd9a0" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#ffd9a0" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`coat-${id}`} gradientUnits="userSpaceOnUse" cx="120" cy="330" r="260">
          <stop offset="0%" stopColor="#f0343a" />
          <stop offset="45%" stopColor="#b8121e" />
          <stop offset="100%" stopColor="#4e040b" />
        </radialGradient>
        <linearGradient id={`gold-${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#fff4cc" />
          <stop offset="28%" stopColor="#e2b458" />
          <stop offset="52%" stopColor="#8e5c1c" />
          <stop offset="76%" stopColor="#f2d488" />
          <stop offset="100%" stopColor="#9a6a24" />
        </linearGradient>
        <radialGradient id={`vignette-${id}`} cx="50%" cy="46%" r="54%">
          <stop offset="68%" stopColor="#000000" stopOpacity="0" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0.5" />
        </radialGradient>
        <filter id={`bokeh-${id}`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" />
        </filter>
        {/* Penselkorn: svagt brus över hela målningen */}
        <filter id={`grain-${id}`} x="0" y="0" width="100%" height="100%">
          <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" seed="11" />
          <feColorMatrix type="matrix" values="0 0 0 0 0.5  0 0 0 0 0.42  0 0 0 0 0.36  0 0 0 -1.4 1.1" />
        </filter>
      </defs>
      <g clipPath={`url(#disc-${id})`}>
        <rect x="0" y="0" width="400" height="400" fill={u("night")} />
        <rect x="0" y="0" width="400" height="400" fill={u("glow")} />
        {bokeh.map((b, i) => (
          <circle key={i} cx={b.x} cy={b.y} r={b.r} fill={b.warm ? "#ffc864" : "#bfe0ff"} opacity={b.o} filter={u("bokeh")} />
        ))}
        {snow.map((s, i) => (
          <circle key={`s${i}`} cx={s.x} cy={s.y} r={s.r} fill="#ffffff" opacity={s.o} />
        ))}
        {/* Axlarna i röd rock */}
        <path d="M-10 420 C 0 350, 40 318, 110 306 C 170 298, 230 298, 290 306 C 360 318, 400 350, 410 420 Z" fill={u("coat")} />
        <g transform="translate(200 212) scale(0.9) translate(-200 -200)">
          <SantaArt />
        </g>
        <rect x="0" y="0" width="400" height="400" filter={u("grain")} opacity={0.1} />
        <circle cx="200" cy="200" r="186" fill={u("vignette")} />
      </g>
      {/* Guldram */}
      <circle cx="200" cy="200" r="188.5" fill="none" stroke="#2a1406" strokeWidth={3} opacity={0.6} />
      <circle cx="200" cy="200" r="193" fill="none" stroke={u("gold")} strokeWidth={8} />
      <circle cx="200" cy="200" r="196.6" fill="none" stroke="#fff6d8" strokeWidth={1} opacity={0.7} />
      <circle cx="200" cy="200" r="189.4" fill="none" stroke="#fff6d8" strokeWidth={0.8} opacity={0.5} />
    </g>
  );
};

/** Hela tomten som ett runt målat porträtt mot en snöig vinterkväll, för hjulets mitt (400 x 400). */
export const SantaBust: React.FC = () => <image href={usePainted("bust", () => <BustArt />)} x={0} y={0} width={400} height={400} />;

/* ------------------------------------------------------------------ */
/* Tomten i släden                                                      */
/* ------------------------------------------------------------------ */

/** Pälskant som en rad tussar längs en linje (för ärmar och rockens framkant). */
const FurTrim: React.FC<{ pts: Pt[]; w: number; seed: number; fill: string }> = ({ pts, w, seed, fill }) => {
  const locks = useMemo(() => {
    const rand = rng(seed);
    const out: Lock[] = [];
    for (let i = 0; i < pts.length - 1; i++) {
      const [ax, ay] = pts[i];
      const [bx, by] = pts[i + 1];
      const segs = Math.max(2, Math.round(Math.hypot(bx - ax, by - ay) / (w * 0.45)));
      for (let k = 0; k < segs; k++) {
        const t = (k + rand()) / segs;
        const h = Math.atan2(by - ay, bx - ax) + Math.PI / 2;
        out.push({
          pts: walk(ax + (bx - ax) * t + (rand() - 0.5) * w * 0.4, ay + (by - ay) * t + (rand() - 0.5) * w * 0.4, h + (rand() - 0.5) * 2.2, w * (0.5 + rand() * 0.4), 5, wave(rand, 0.3, 0.6, 5)),
          w: w * (0.5 + rand() * 0.2),
        });
      }
    }
    return out;
  }, [pts, w, seed]);
  return (
    <g>
      <path d={curve(pts)} stroke={fill} strokeWidth={w * 0.8} strokeLinecap="round" fill="none" />
      {paintLocks(locks, { fill, shadow: "#7d879c", light: "#ffffff", strand: "#9aa3b6" }, "ft", { strands: 0 })}
    </g>
  );
};

const CUFF_L: Pt[] = [[40, -8], [54, -12]];
const CUFF_R: Pt[] = [[146, 34], [148, 48]];
const PLACKET: Pt[] = [[95, 30], [95, 50], [95, 70]];
const HEM: Pt[] = [[48, 68], [95, 71], [142, 68]];

/**
 * Tomten som sitter i släden, i samma målade stil som porträttet: rock i
 * röd sammet med pälskanter, svart bälte med guldspänne, ena handen vinkar
 * och den andra håller tömmarna. Ritas i slädens koordinater.
 */
export const SleighSanta: React.FC = () => {
  const id = useId().replace(/:/g, "");
  const u = (name: string) => `url(#${name}-${id})`;
  return (
    <g>
      <defs>
        <radialGradient id={`suit-${id}`} gradientUnits="userSpaceOnUse" cx="70" cy="18" r="80">
          <stop offset="0%" stopColor="#ff5a4c" />
          <stop offset="35%" stopColor="#dc1f28" />
          <stop offset="75%" stopColor="#9a0e19" />
          <stop offset="100%" stopColor="#52050c" />
        </radialGradient>
        <radialGradient id={`sfur-${id}`} gradientUnits="userSpaceOnUse" cx="60" cy="10" r="110">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="55%" stopColor="#eeeef1" />
          <stop offset="100%" stopColor="#a7afc0" />
        </radialGradient>
        <linearGradient id={`belt-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3a2620" />
          <stop offset="45%" stopColor="#140a08" />
          <stop offset="100%" stopColor="#050202" />
        </linearGradient>
        <linearGradient id={`buckle-${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#fff4cc" />
          <stop offset="50%" stopColor="#d9a84a" />
          <stop offset="100%" stopColor="#8a5a1c" />
        </linearGradient>
        <radialGradient id={`glove-${id}`} cx="35%" cy="30%" r="80%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="60%" stopColor="#e6e7ec" />
          <stop offset="100%" stopColor="#9aa2b4" />
        </radialGradient>
        <radialGradient id={`leather-${id}`} cx="35%" cy="30%" r="80%">
          <stop offset="0%" stopColor="#6a4a3c" />
          <stop offset="100%" stopColor="#1a0e0a" />
        </radialGradient>
      </defs>

      {/* Rocken */}
      <path d="M46 72 C 40 44, 52 18, 94 12 C 134 14, 150 40, 144 72 Z" fill={u("suit")} />
      <path d="M58 30 C 66 20, 78 16, 90 15" stroke="#ff9a88" strokeWidth={3} fill="none" opacity={0.5} strokeLinecap="round" />
      <path d="M136 36 C 142 48, 144 60, 142 70" stroke="#3a0207" strokeWidth={5} fill="none" opacity={0.4} strokeLinecap="round" />
      <FurTrim pts={PLACKET} w={11} seed={71} fill={u("sfur")} />
      {/* Bälte med guldspänne */}
      <path d="M46 54 C 70 58, 120 58, 145 54 L 145 63 C 120 67, 70 67, 46 63 Z" fill={u("belt")} />
      <path d="M48 55.4 C 72 59, 118 59, 143 55.4" stroke="#8a6a5a" strokeWidth={0.8} fill="none" opacity={0.7} />
      <rect x="86" y="52.5" width="18" height="13" rx="2" fill="none" stroke={u("buckle")} strokeWidth={3} />
      <path d="M88 54 h 6" stroke="#fffbe6" strokeWidth={1} opacity={0.8} />
      <FurTrim pts={HEM} w={12} seed={72} fill={u("sfur")} />

      {/* Armen som håller tömmarna */}
      <path d="M126 24 C 138 26, 148 32, 150 38 L 144 48 C 138 42, 128 40, 120 40 Z" fill={u("suit")} />
      <FurTrim pts={CUFF_R} w={10} seed={73} fill={u("sfur")} />
      <path d="M150 36 C 158 34, 164 40, 162 46 C 160 52, 152 52, 150 48 Z" fill={u("leather")} />
      <path d="M153 38 C 157 37, 160 39, 160 41" stroke="#b08a74" strokeWidth={1} fill="none" opacity={0.6} strokeLinecap="round" />

      {/* Huvudet: samma målade porträtt som i hjulet */}
      <SantaPortrait x={42} y={-74} size={104} />

      {/* Armen som vinkar */}
      <g>
        <animateTransform attributeName="transform" type="rotate" values="0 62 28; -14 62 28; 0 62 28" dur="1.4s" repeatCount="indefinite" />
        <path d="M66 32 C 54 22, 46 8, 44 -8 L 56 -12 C 58 0, 64 12, 76 22 Z" fill={u("suit")} />
        <FurTrim pts={CUFF_L} w={10} seed={74} fill={u("sfur")} />
        <path d="M40 -12 C 34 -20, 36 -32, 44 -34 C 46 -40, 54 -40, 56 -34 C 62 -32, 64 -22, 58 -14 C 54 -10, 44 -8, 40 -12 Z" fill={u("glove")} />
        <path d="M34 -24 C 30 -28, 30 -34, 36 -34 C 38 -32, 38 -28, 38 -24 Z" fill={u("glove")} />
        <path d="M44 -34 C 46 -30, 46 -24, 45 -20 M 50 -36 C 51 -30, 51 -24, 50 -20" stroke="#aeb5c5" strokeWidth={1} fill="none" opacity={0.7} strokeLinecap="round" />
      </g>
    </g>
  );
};

/* ------------------------------------------------------------------ */
/* Tomten dansar i släden                                               */
/* ------------------------------------------------------------------ */

/**
 * En koreografi: vinklar per halvtakt à 0,5 s (120 bpm). 0 = armen hänger
 * rakt ner, positivt = medurs. Alla listor har samma längd (takter + 1).
 */
interface Choreography {
  armR: number[];
  armL: number[];
  head: number[];
  hips: number[];
  /** Hela kroppen kring bröstet: 180 = upp och ner (headspin), 260 = liggande freeze. */
  body?: number[];
  /** Hur mycket benen särar (grader), för headspin och freeze. */
  split?: number[];
  /** Snurr kring den lodräta axeln (scaleX), som tider 0..1 och värden. */
  spin: { times: number[]; values: number[] };
}

/**
 * Vanliga dansen, 32 halvtakter = 16 s:
 *   0-4   disco: pekar upp mot himlen och ner över bröstet
 *   5-8   raise the roof
 *   9-11  dab
 *   12-15 Y-M-C-A
 *   16-19 robot: stela, ryckiga armar
 *   20-23 zombie (Thriller): båda armarna framåt, hasar i sidled
 *   24-27 floss
 *   28-29 pekar på månen
 *   30-32 snurr och "ta-da!"
 */
const GROOVE: Choreography = {
  //    disco                       roof                    dab                 Y     M     C     A     robot               zombie              floss            måne        ta-da
  armR: [0, -140, 45, -140, 45, -165, -140, -170, -140, -120, -120, -120, -150, -195, -100, -209, -90, -90, 0, -90, -95, -85, -95, -85, 30, -30, 30, -30, -150, -150, -120, -120, 0],
  armL: [0, 20, 20, 20, 20, 165, 140, 170, 140, -124, -124, -124, 150, 195, -40, 208, 90, 0, 90, 90, -80, -95, -80, -95, 30, -30, 30, -30, 20, 20, 120, 120, 0],
  head: [0, -6, 6, -6, 6, -4, 4, -4, 4, 16, 16, 16, 0, 0, -8, 0, 0, -10, 10, 0, 8, -4, 8, -4, -6, 6, -6, 6, -10, -10, 0, 0, 0],
  hips: [0, 3, -3, 3, -3, 0, 0, 0, 0, -5, -5, -5, 0, 0, 4, 0, 0, 0, 0, 0, -4, 4, -4, 4, -6, 6, -6, 6, 2, 2, 0, 0, 0],
  spin: { times: [0, 0.9, 0.925, 0.95, 1], values: [1, 1, -1, 1, 1] },
};

/** Headspinnens snurr: åtta varv under 4 s medan han står på huvudet. */
const headspin = (() => {
  const times = [0, 0.25];
  const values = [1, 1];
  for (let k = 1; k <= 16; k++) {
    times.push(0.25 + (k * 0.25) / 16);
    values.push(k % 2 ? -1 : 1);
  }
  return { times: [...times, 1], values: [...values, 1] };
})();

/**
 * Breakdance, 32 halvtakter = 16 s:
 *   0-5   toprock: korsar och öppnar armarna i takt
 *   6-7   går ner på huvudet
 *   7-16  headspin med benen i V och armarna ute
 *   17-18 upp igen, armarna i luften
 *   19-20 toprock
 *   21-25 freeze: liggande i luften på ena handen, benen isär
 *   26-29 toprock
 *   30-32 b-boy-pose med korsade armar och en nick
 */
const BREAK: Choreography = {
  //    toprock                     ner   headspin                                                   upp         toprock   freeze                        toprock               pose
  body: [0, 0, 0, 0, 0, 0, 20, 180, 180, 180, 180, 180, 180, 180, 180, 180, 180, 360, 360, 360, 360, 260, 262, 258, 262, 260, 360, 360, 360, 360, 360, 360, 360],
  armR: [0, 60, -40, 60, -40, 60, -90, -90, -100, -80, -100, -80, -100, -80, -100, -80, -90, -160, -160, 60, -40, -150, -150, -150, -150, -150, -90, 60, -40, 60, 70, 70, 0],
  armL: [0, -60, 40, -60, 40, -60, 90, 90, 100, 80, 100, 80, 100, 80, 100, 80, 90, 160, 160, -60, 40, 20, 20, 20, 20, 20, 90, -60, 40, -60, -70, -70, 0],
  head: [0, -6, 6, -6, 6, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, -6, 6, -8, -8, -8, -8, -8, 0, -6, 6, -6, 8, 8, 0],
  hips: [0, 4, -4, 4, -4, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 4, -4, 0, 0, 0, 0, 0, 0, 4, -4, 4, 0, 0, 0],
  split: [0, 0, 0, 0, 0, 0, 0, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 0, 0, 0, 0, 30, 30, 30, 30, 30, 0, 0, 0, 0, 0, 0, 0],
  spin: headspin,
};

const DANCE_DUR = 16;

/** Rotation som följer koreografin. (cx, cy) = vridpunkten. */
const Choreo: React.FC<{ angles: number[]; cx?: number; cy?: number }> = ({ angles, cx = 0, cy = 0 }) => {
  const beats = angles.length - 1;
  return (
    <animateTransform
      attributeName="transform"
      type="rotate"
      dur={`${DANCE_DUR}s`}
      repeatCount="indefinite"
      calcMode="spline"
      keyTimes={angles.map((_, i) => (i / beats).toFixed(4)).join(";")}
      keySplines={Array.from({ length: beats }, () => "0.45 0 0.2 1").join(";")}
      values={angles.map(a => `${a} ${cx} ${cy}`).join(";")}
    />
  );
};

/** En arm som hänger från axeln i (0, 0): ärm, pälskant och vit handske. */
const DanceArm: React.FC<{ u: (name: string) => string; seed: number }> = ({ u, seed }) => (
  <g>
    <path d="M-9 -2 C -10 14, -9 28, -7 38 L 7 38 C 9 28, 10 14, 9 -2 Z" fill={u("suit")} />
    <path d="M4 2 C 6 14, 6 26, 5 36" stroke="#3a0207" strokeWidth={3} fill="none" opacity={0.35} strokeLinecap="round" />
    <FurTrim pts={ARM_CUFF} w={10} seed={seed} fill={u("sfur")} />
    <path d="M-7 44 C -9 52, -4 58, 1 58 C 7 58, 10 52, 8 45 C 6 41, -5 40, -7 44 Z" fill={u("glove")} />
    <path d="M-7 47 C -12 47, -13 52, -9 54" stroke={u("glove")} strokeWidth={4.4} strokeLinecap="round" fill="none" />
  </g>
);

const ARM_CUFF: Pt[] = [[-8, 38], [8, 38]];
const DANCE_PLACKET: Pt[] = [[96, -8], [96, 14], [96, 36]];
const DANCE_HEM: Pt[] = [[50, 42], [96, 45], [142, 42]];

/**
 * Tomten står upp i släden och dansar efter en koreografi: studsar i takt,
 * svänger på höfterna, nickar och rör armarna; i breakdance går han upp och
 * ner på huvudet och gör en freeze, och då syns även de svarta stövlarna
 * som annars är dolda i släden. Samma målade porträtt och rock som när
 * han sitter. Ritas i slädens koordinater.
 */
const DanceFigure: React.FC<{ moves: Choreography }> = ({ moves }) => {
  const id = useId().replace(/:/g, "");
  const u = (name: string) => `url(#${name}-${id})`;
  const zero = moves.armR.map(() => 0);
  return (
    <g>
      <defs>
        <radialGradient id={`suit-${id}`} gradientUnits="userSpaceOnUse" cx="76" cy="-14" r="90">
          <stop offset="0%" stopColor="#ff5a4c" />
          <stop offset="35%" stopColor="#dc1f28" />
          <stop offset="75%" stopColor="#9a0e19" />
          <stop offset="100%" stopColor="#52050c" />
        </radialGradient>
        <radialGradient id={`sfur-${id}`} gradientUnits="userSpaceOnUse" cx="60" cy="-20" r="120">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="55%" stopColor="#eeeef1" />
          <stop offset="100%" stopColor="#a7afc0" />
        </radialGradient>
        <linearGradient id={`belt-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3a2620" />
          <stop offset="45%" stopColor="#140a08" />
          <stop offset="100%" stopColor="#050202" />
        </linearGradient>
        <linearGradient id={`buckle-${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#fff4cc" />
          <stop offset="50%" stopColor="#d9a84a" />
          <stop offset="100%" stopColor="#8a5a1c" />
        </linearGradient>
        <radialGradient id={`glove-${id}`} cx="35%" cy="30%" r="80%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="60%" stopColor="#e6e7ec" />
          <stop offset="100%" stopColor="#9aa2b4" />
        </radialGradient>
        <linearGradient id={`boot-${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#4a3630" />
          <stop offset="40%" stopColor="#150a08" />
          <stop offset="100%" stopColor="#050202" />
        </linearGradient>
      </defs>

      {/* Studs i takt */}
      <g>
        <animateTransform
          attributeName="transform"
          type="translate"
          values="0 0; 0 -6; 0 0"
          keyTimes="0;0.4;1"
          calcMode="spline"
          keySplines="0.3 0 0.2 1;0.5 0 0.7 1"
          dur="0.5s"
          repeatCount="indefinite"
        />
        {/* Snurr kring den lodräta axeln */}
        <g transform="translate(95 0)">
          <g>
            <animateTransform
              attributeName="transform"
              type="scale"
              values={moves.spin.values.map(v => `${v} 1`).join(";")}
              keyTimes={moves.spin.times.map(t => t.toFixed(4)).join(";")}
              dur={`${DANCE_DUR}s`}
              repeatCount="indefinite"
            />
            <g transform="translate(-95 0)">
              {/* Hela kroppen (headspin och freeze) */}
              <g>
                <Choreo angles={moves.body ?? zero} cx={95} cy={-16} />
                {/* Höfterna svänger kring fötterna */}
                <g>
                  <Choreo angles={moves.hips} cx={95} cy={98} />
                  {/* Benen pumpar i otakt och särar i headspin och freeze */}
                  {[
                    [80, 0, 1],
                    [110, -0.25, -1],
                  ].map(([lx, begin, side]) => (
                    <g key={lx}>
                      <Choreo angles={(moves.split ?? zero).map(a => a * side)} cx={lx} cy={40} />
                      <g>
                        <animateTransform
                          attributeName="transform"
                          type="rotate"
                          values={`0 ${lx} 40; ${side * 7} ${lx} 40; 0 ${lx} 40`}
                          dur="0.5s"
                          begin={`${begin}s`}
                          repeatCount="indefinite"
                        />
                        <path d={`M${lx - 12} 36 C ${lx - 13} 60, ${lx - 11} 82, ${lx - 9} 100 L ${lx + 9} 100 C ${lx + 11} 82, ${lx + 13} 60, ${lx + 12} 36 Z`} fill={u("suit")} />
                        <path d={`M${lx + 6} 44 C ${lx + 8} 62, ${lx + 8} 80, ${lx + 7} 96`} stroke="#3a0207" strokeWidth={3} fill="none" opacity={0.35} strokeLinecap="round" />
                        {/* Stöveln */}
                        <path d={`M${lx - 10} 92 H${lx + 10} L${lx + 11} 101 C ${lx + 20} 101, ${lx + 22} 106, ${lx + 21} 110 H${lx - 11} Z`} fill={u("boot")} />
                        <path d={`M${lx - 7} 95 C ${lx - 6} 100, ${lx - 6} 104, ${lx - 6} 107`} stroke="#8a6a5a" strokeWidth={1.2} fill="none" opacity={0.6} strokeLinecap="round" />
                        <rect x={lx - 11} y={89} width="22" height="6" rx="3" fill={u("sfur")} />
                      </g>
                    </g>
                  ))}

                  {/* Rocken */}
                  <path d="M48 46 C 42 10, 56 -16, 96 -22 C 136 -16, 150 10, 144 46 Z" fill={u("suit")} />
                  <path d="M60 -4 C 68 -14, 80 -18, 92 -19" stroke="#ff9a88" strokeWidth={3} fill="none" opacity={0.5} strokeLinecap="round" />
                  <path d="M136 4 C 142 16, 144 30, 142 42" stroke="#3a0207" strokeWidth={5} fill="none" opacity={0.4} strokeLinecap="round" />
                  <FurTrim pts={DANCE_PLACKET} w={11} seed={81} fill={u("sfur")} />
                  {/* Bälte med guldspänne */}
                  <path d="M47 18 C 70 22, 122 22, 145 18 L 145 27 C 122 31, 70 31, 47 27 Z" fill={u("belt")} />
                  <path d="M49 19.4 C 72 23, 120 23, 143 19.4" stroke="#8a6a5a" strokeWidth={0.8} fill="none" opacity={0.7} />
                  <rect x="87" y="16.5" width="18" height="13" rx="2" fill="none" stroke={u("buckle")} strokeWidth={3} />
                  <path d="M89 18 h 6" stroke="#fffbe6" strokeWidth={1} opacity={0.8} />
                  <FurTrim pts={DANCE_HEM} w={12} seed={82} fill={u("sfur")} />

                  {/* Huvudet nickar i takt */}
                  <g>
                    <Choreo angles={moves.head} cx={95} cy={-16} />
                    <SantaPortrait x={44} y={-108} size={104} />
                  </g>

                  {/* Armarna */}
                  <g transform="translate(58 -10)">
                    <g>
                      <Choreo angles={moves.armL} />
                      <DanceArm u={u} seed={83} />
                    </g>
                  </g>
                  <g transform="translate(134 -10)">
                    <g>
                      <Choreo angles={moves.armR} />
                      <DanceArm u={u} seed={84} />
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
      </g>
    </g>
  );
};

/** Vanliga dansen: disco, raise the roof, dab, Y-M-C-A, robot, zombie, floss och "ta-da!". */
export const DancingSanta: React.FC = () => <DanceFigure moves={GROOVE} />;

/** Breakdance: toprock, headspin, freeze och b-boy-pose. */
export const BreakdanceSanta: React.FC = () => <DanceFigure moves={BREAK} />;
